"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Sidebar, MobileSidebarDrawer } from "../../components/layout/AdminSidebar";
import { fetchSlidesAll } from "../../lib/sliderApi";

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  accent_color: string;
  bg_gradient: string;
  created_at: string;
  is_active: boolean;
}

const PLACEHOLDER =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2U1ZTVlNSI+PC9zdmc+";

const SlidesManagement: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const navigate = useNavigate();

  /* ---------------- LOAD ---------------- */
  const loadSlides = async () => {
   try {
    setLoading(true);
    const data = await fetchSlidesAll();

    setSlides(data); // or data.filter(s => s.is_active)
  } catch (err: any) {
    setMessage({ type: "error", text: err.message });
  } finally {
    setLoading(false);
  }
  };

  useEffect(() => {
    loadSlides();
  }, []);

  /* ---------------- SELECT ---------------- */
  const toggleSelect = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = (checked: boolean) => {
    if (checked) setSelected(slides.map((s) => s.id));
    else setSelected([]);
  };

  /* ---------------- DELETE ---------------- */
 const toggleSlide = async (id: number) => {
  try {
    await fetch(`${process.env.PUBLIC_API || 'https://thequran4all.com/api'}/admin/slides/${id}/toggle`, {
      method: "Delete",
    });

    setMessage({
      type: "success",
      text: "Slide updated successfully",
    });

    loadSlides();
  } catch (err: any) {
    setMessage({ type: "error", text: err.message });
  }
};
const getImageUrl = (image?: string) => {
  if (!image) return PLACEHOLDER;

  if (image.startsWith("globcomGallary")) {
    return `${process.env.PUBLIC_API || 'https://thequran4all.com/api'}/uploads/${image}`;
  }

  return image;
};
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <MobileSidebarDrawer
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* MOBILE HEADER */}
     <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-30">
            <div className="flex items-center justify-between px-4 py-3">
              <button onClick={() => setIsSidebarOpen(true)} className="p-2">
                <Menu size={24} />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Books Management</h1>
              <div className="w-10" />
            </div>
          </div>
<div className="lg:ml-64 pt-16 lg:pt-0">
        <div className="px-4 py-6 lg:px-8 lg:py-8">
          {message && (
            <div className={`p-4 rounded-lg mb-6 text-sm font-medium ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span>{message.text}</span>
                <button onClick={() => setMessage(null)} className="hover:opacity-70">
                  <X size={16} />
                </button>
              </div>
            </div>
          )}
          

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 lg:mb-8">
            <h1 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4 lg:mb-0 hidden lg:block">
              Content Management
            </h1>
            
            {/* ACTION BUTTONS - "Add to Slide" + Delete */}
            <div className="flex flex-wrap gap-3">
              <button 
               onClick={() => navigate("/admin/addslidepage")}
                 
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Add Slide
              </button>
             
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading Slide...</p>
            </div>
          ) : (
             <>
            {/* TABLE (same structure as books page) */}
            <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">

              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                  
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Image
                    </th>

                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Title
                    </th>

                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Subtitle
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">
                      Created
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {slides.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">

                  
                      <td className="px-6 py-4">
                        <img
                          src={getImageUrl(s.image)}
                          className="w-12 h-12 object-cover rounded"
                        />
                      </td>

                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {s.title}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {s.subtitle}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleSlide(s.id)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            s.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {s.is_active ? "Visible" : "Hidden"}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(s.created_at).toLocaleDateString()}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBILE */}
            <div className="md:hidden space-y-3">
              {slides.map((s) => (
                <div key={s.id} className="bg-white border rounded-lg p-4">

                  <div className="flex gap-3">

                    <input
                      type="checkbox"
                      checked={selected.includes(s.id)}
                      onChange={() => toggleSelect(s.id)}
                    />

                    <img
                      src={s.image || PLACEHOLDER}
                      className="w-16 h-16 object-cover rounded"
                    />

                    <div>
                      <h3 className="font-semibold">{s.title}</h3>
                      <p className="text-sm text-gray-600">{s.subtitle}</p>
                    </div>

                  </div>

                </div>
              ))}
            </div>
          </>
          )}
        </div>
      </div>
 
 
    </div>
  );
};

export default SlidesManagement;